import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, X } from "lucide-react";
import { http, formatApiError } from "../../lib/api";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (s, k) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + k); return iso(d); };
const fr = (s) => s.split("-").reverse().slice(0, 2).join("/");

const DayTargets = ({ base, value, onChange }) => {
  const [y, m] = base.split("-").map(Number);
  const n = new Date(y, m, 0).getDate();
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  return (
    <div data-testid="copy-day-targets">
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-400 mb-1">{["L", "M", "M", "J", "V", "S", "D"].map((l, i) => <div key={i}>{l}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: n }, (_, i) => {
          const d = `${y}-${pad(m)}-${pad(i + 1)}`, isBase = d === base, on = value.includes(d);
          return <button key={d} type="button" disabled={isBase} data-testid={`copy-target-${d}`} onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d].sort())}
            className={`h-8 rounded-md text-xs font-bold border transition-colors ${isBase ? "bg-slate-900 text-white border-slate-900" : on ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200 hover:border-sky-400"}`}>{i + 1}</button>;
        })}
      </div>
    </div>
  );
};

const weekOptions = (weekStart, month) =>
  [1, 2, 3, 4, 5].map((k) => ({ k, from: addDays(weekStart, 7 * k), to: addDays(weekStart, 7 * k + 6) })).filter((w) => w.from.slice(0, 7) === month || w.to.slice(0, 7) === month);

export const CopyVacationsDialog = ({ mode, siteId, siteName, sources, base, viewMonth, onClose, onDone }) => {
  const [picked, setPicked] = useState(sources.map((v) => v.id));
  const [dates, setDates] = useState([]);
  const month = mode === "week" ? viewMonth || base.slice(0, 7) : base.slice(0, 7);
  const weeks = useMemo(() => (mode === "week" ? weekOptions(base, month) : []), [mode, base, month]);
  const [wk, setWk] = useState(weeks.map((w) => w.k));
  const [busy, setBusy] = useState(false);
  const items = useMemo(() => {
    const src = sources.filter((v) => picked.includes(v.id));
    if (mode === "day") return dates.flatMap((d) => src.map((v) => ({ source_id: v.id, date: d })));
    return wk.flatMap((k) => src.map((v) => ({ source_id: v.id, date: addDays(v.date, 7 * k) }))).filter((x) => x.date.slice(0, 7) === month);
  }, [mode, sources, picked, dates, wk, month]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/vacations/copy", { site_id: siteId, month, items });
      toast.success(`${data.created} vacation(s) copiée(s) sur ${data.days} jour(s), à pourvoir`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[92vh] flex flex-col" data-testid="copy-vacations-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Copy className="h-5 w-5 text-sky-700" />
          <h3 className="font-display text-lg font-bold flex-1">{mode === "day" ? `Copier la journée du ${fr(base)}` : `Copier la semaine du ${fr(base)}`}</h3>
          <button onClick={onClose} data-testid="copy-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-xs text-slate-500">Site <b>{siteName}</b> · les copies sont créées <b>à pourvoir</b> (sans agent), mêmes horaires, activité et notes.</p>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">Vacations à copier ({picked.length}/{sources.length})</div>
            <ul className="border border-slate-200 rounded-md divide-y max-h-48 overflow-y-auto" data-testid="copy-sources">
              {sources.map((v) => (
                <li key={v.id}><label className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={picked.includes(v.id)} onChange={() => toggle(v.id)} data-testid={`copy-src-${v.id}`} />
                  {mode === "week" && <span className="text-xs font-bold w-12">{fr(v.date)}</span>}
                  <span>{v.start_time}–{v.end_time}</span><span className="text-slate-500 text-xs">{v.activity || ""}</span>
                </label></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">{mode === "day" ? "Copier vers les jours" : "Copier vers les semaines suivantes du mois"}</div>
            {mode === "day" ? <DayTargets base={base} value={dates} onChange={setDates} /> : weeks.length === 0
              ? <p className="text-sm text-slate-500" data-testid="copy-no-week">Aucune semaine suivante dans ce mois.</p>
              : <div className="space-y-1" data-testid="copy-weeks">{weeks.map((w) => (
                  <label key={w.k} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={wk.includes(w.k)} data-testid={`copy-week-${w.k}`}
                    onChange={() => setWk((p) => (p.includes(w.k) ? p.filter((x) => x !== w.k) : [...p, w.k]))} />Semaine du {fr(w.from)} au {fr(w.to)}</label>
                ))}{weeks.some((w) => w.to.slice(0, 7) !== month || w.from.slice(0, 7) !== month) && <p className="text-[11px] text-slate-500">Seuls les jours du mois affiché sont copiés.</p>}</div>}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <span className="text-xs text-slate-600 mr-auto" data-testid="copy-summary">{items.length} vacation(s) seront créées</span>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300" data-testid="copy-cancel">Annuler</button>
          <button disabled={busy || !items.length || items.length > 500} onClick={go} className="px-4 py-2 text-sm font-bold rounded-md bg-sky-600 text-white disabled:opacity-50" data-testid="copy-submit">{busy ? "Copie…" : "Copier"}</button>
        </div>
      </div>
    </div>
  );
};
